import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRemoteModule } from '@/hooks/useRemoteModule';
import { KAIROS_HOST_CAPABILITIES_V1 } from '@/lib/remoteLoader';

/**
 * Story 123.5 — mounts a capability's federated remote into the shell.
 *
 * The shell gives the remote a bare <div> and calls its `mount(el, ctx)`
 * (federation contract in remoteLoader.ts). The remote renders into that div
 * with its OWN React root / bundled React, so the shell's React 19 tree and a
 * remote's (e.g. React 18.3) tree never cross — no "invalid hook call". The
 * remote reads session/tenant from the global __KAIROS_SESSION__ bridge.
 *
 * Renders nothing when the tenant lacks the capability (denied) or no remote is
 * registered for it (the env-driven registry, 123.5).
 */
export function RemoteHost({ capabilityKey }: { capabilityKey: string }) {
  const { module, loading, error, denied, hasEntry } = useRemoteModule(capabilityKey);
  const { t } = useTranslation();
  const elRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = elRef.current;
    if (!module || !el) return;
    let unmount: (() => void) | undefined;
    try {
      unmount = module.mount(el, {
        capabilityKey,
        basePath: `/r/${capabilityKey}`,
        host: KAIROS_HOST_CAPABILITIES_V1,
      });
    } catch (e) {
      // A remote that throws on mount must not take down the shell.
      console.error(`remote ${capabilityKey} failed to mount`, e);
    }
    return () => {
      try {
        unmount?.();
      } catch (e) {
        console.error(`remote ${capabilityKey} failed to unmount`, e);
      }
    };
  }, [module, capabilityKey]);

  if (denied || !hasEntry) return null;
  if (error) {
    return (
      <div data-testid={`remote-error-${capabilityKey}`} className="p-6 text-sm text-muted-foreground">
        {t('remote.loadError', 'No se pudo cargar el módulo.')}
      </div>
    );
  }
  if (loading) {
    return <div data-testid={`remote-loading-${capabilityKey}`} className="p-6" aria-busy="true" />;
  }
  return <div ref={elRef} data-remote={capabilityKey} className="h-full" />;
}

/**
 * Router entry for `/r/:capabilityKey` — mounts the federated remote registered
 * for the capability. The capability gate lives in useRemoteModule (denied →
 * renders nothing); this wrapper just adapts the route param.
 */
export function RemoteRoute() {
  const { capabilityKey } = useParams<{ capabilityKey: string }>();
  if (!capabilityKey) return null;
  return <RemoteHost capabilityKey={capabilityKey} />;
}
